import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plug, RefreshCw, AlertTriangle } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { RolePill } from '../../components/ui/Pills'
import { TextField, PasswordField } from '../../components/ui/Field'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { servexaIntegrationView } from '../../lib/types'
import {
  buildSavePayload,
  describeBaseUrlSource,
  describeConnection,
  describeKeyState,
  describeWebhookSecretState,
  initialBaseUrl,
  initialChatWidgetUrl,
  initialPublicIssueCode,
  validateSaveInputs,
} from '../../lib/servexaIntegration'

const QUERY_KEY = ['super-admin', 'servexa-integration']
const BASE = '/super-admin/servexa'

// Map the semantic connection tone to a shared RolePill tone, so the status
// pill reads in the same visual language as every other status pill in the
// platform panel (see components/ui/Pills.jsx).
const PILL_TONE = {
  success: 'success',
  info: 'info',
  warning: 'amber',
  danger: 'danger',
  neutral: 'muted',
}

/**
 * [Servexa] Super Admin Integration panel (spec A9/A20).
 *
 * Mounted at /super-admin/servexa-integration behind
 * `ProtectedRoute allowRoles={['super_admin']}`; renders content only (the
 * SuperAdminLayout owns the shell). Styled to match the platform Settings
 * pages (GoogleSsoSettings / SOP Integration): the shared page container,
 * an h1 header with the status pill on the right, and a single card of
 * fields — no bespoke visual language.
 *
 * All Servexa calls are server-to-server: this page only ever talks to our
 * own /super-admin/servexa backend, which holds the credential and calls
 * Servexa with `Authorization: Bearer <key>`. The raw key never leaves the
 * server — the view carries only `apiKeyMasked`.
 */
export function ServexaIntegrationPage() {
  const qc = useQueryClient()
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [apiKeyDirty, setApiKeyDirty] = useState(false)
  // null = untouched → show the backend's effective value; a string = the
  // admin's in-progress edit.
  const [baseUrlEdit, setBaseUrlEdit] = useState(null)
  // Public Issue Code (A6a) — non-secret, editable like the base URL:
  // null = show the backend value, a string = the admin's in-progress edit.
  const [publicIssueCodeEdit, setPublicIssueCodeEdit] = useState(null)
  // Chat Widget URL (A8b value 4, N26) — non-secret, editable the same way.
  const [chatWidgetUrlEdit, setChatWidgetUrlEdit] = useState(null)
  // Webhook signing secret (A13.4) — a secret, tracked like the API key.
  const [webhookSecretInput, setWebhookSecretInput] = useState('')
  const [webhookSecretDirty, setWebhookSecretDirty] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  const viewQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get(BASE)
      return servexaIntegrationView.parse(res.data)
    },
  })

  const view = viewQuery.data ?? null
  const baseUrlValue = baseUrlEdit === null ? initialBaseUrl(view) : baseUrlEdit
  const publicIssueCodeValue =
    publicIssueCodeEdit === null ? initialPublicIssueCode(view) : publicIssueCodeEdit
  const chatWidgetUrlValue =
    chatWidgetUrlEdit === null ? initialChatWidgetUrl(view) : chatWidgetUrlEdit

  /** Every mutation returns the FRESH panel view (the backend saves/requests
   *  then re-reads GET /integrations/connection). Write it straight into the
   *  cache so the displayed status is the real one — never a flag inferred
   *  from "the call succeeded" (spec A9.4). */
  function applyView(res) {
    const next = servexaIntegrationView.parse(res.data)
    qc.setQueryData(QUERY_KEY, next)
    return next
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.put(
        BASE,
        buildSavePayload({
          apiKeyInput,
          apiKeyDirty,
          baseUrlInput: baseUrlValue,
          publicIssueCodeInput: publicIssueCodeValue,
          chatWidgetUrlInput: chatWidgetUrlValue,
          webhookSecretInput,
          webhookSecretDirty,
        }),
      )
      return applyView(res)
    },
    onSuccess: (next) => {
      // Clear the transient key input (no plaintext key lingers) and drop the
      // base-URL edit back to the freshly-saved effective value.
      setApiKeyInput('')
      setApiKeyDirty(false)
      setBaseUrlEdit(null)
      setPublicIssueCodeEdit(null)
      setChatWidgetUrlEdit(null)
      setWebhookSecretInput('')
      setWebhookSecretDirty(false)
      setFieldErrors({})
      toast.success(
        next.connected
          ? 'Saved. Servexa connection is active.'
          : 'Saved. Connection status refreshed.',
      )
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) {
        setFieldErrors({
          apiKey: fields.apiKey,
          baseUrl: fields.baseUrl,
          publicIssueCode: fields.publicIssueCode,
          chatWidgetUrl: fields.chatWidgetUrl,
          webhookSecret: fields.webhookSecret,
        })
      }
      toast.error(
        fields?.baseUrl ??
          fields?.apiKey ??
          fields?.webhookSecret ??
          fields?.publicIssueCode ??
          fields?.chatWidgetUrl ??
          message,
      )
    },
  })

  const requestMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`${BASE}/connection/request`, {})
      return applyView(res)
    },
    onSuccess: (next) =>
      toast.success(`Connection ${describeConnection(next).label.toLowerCase()}.`),
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`${BASE}/connection/refresh`)
      return applyView(res)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const busy =
    saveMutation.isPending || requestMutation.isPending || refreshMutation.isPending

  function handleSave() {
    const { valid, errors } = validateSaveInputs({
      apiKeyInput,
      apiKeyDirty,
      baseUrlInput: baseUrlValue,
      hasApiKey: Boolean(view?.hasApiKey),
      chatWidgetUrlInput: chatWidgetUrlValue,
      // An emptied Base URL field falls back to the environment default on
      // the server, so that is the Base URL the Chat Widget URL is judged by.
      effectiveBaseUrl: view?.envBaseUrl ?? '',
      webhookSecretInput,
      webhookSecretDirty,
    })
    setFieldErrors(errors)
    if (!valid) return
    saveMutation.mutate()
  }

  const keyState = describeKeyState(view)
  const webhookSecretState = describeWebhookSecretState(view)
  const conn = describeConnection(view)

  // Field helper text (kept in the shared FieldFooter — no bespoke note rows).
  const keyHelper = keyState.configured
    ? `A key is configured${keyState.savedAt ? ` — saved ${formatDateTime(keyState.savedAt)}` : ''}. Sent as Authorization: Bearer server-side; encrypted at rest, never shown again.`
    : 'Sent as Authorization: Bearer server-side. Encrypted at rest (AES-256-GCM); never returned after saving.'
  const baseUrlHelper = view
    ? `${describeBaseUrlSource(view)}${view.envBaseUrl ? ` Environment default: ${view.envBaseUrl}` : ''}`
    : undefined
  const publicIssueCodeHelper =
    'Not a secret. Powers the pre-login "Report an Issue" form on the sign-in screens.'
  const chatWidgetUrlHelper =
    "Not a secret. The exact servexa-chat-widget.js URL from Servexa's setup email — never build it from the Base URL. Leave empty if Chat is not used."
  const webhookSecretHelper = webhookSecretState.configured
    ? 'A signing secret is configured — leave blank to keep. Verifies inbound status webhooks; encrypted at rest.'
    : 'Optional. Verifies inbound status webhooks (whsec_…). Stored encrypted; never shown again.'

  return (
    <div className="space-y-6">
      <Breadcrumb current="Servexa Integration" homePath="/super-admin/dashboard" />

      {/* Page header — title + subtitle on the left, live status + Refresh on
       *  the right, mirroring the Settings-tab header pattern. */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Plug size={20} className="text-[color:var(--color-accent-strong)]" />
            Servexa Integration
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            Connect TaskMagix support to Servexa. The key and Base URL are stored
            on the server — the browser only ever talks to TaskMagix.
          </p>
        </div>
        {view ? (
          <div className="flex items-center gap-2 shrink-0">
            <RolePill label={conn.label} tone={PILL_TONE[conn.tone] ?? 'muted'} />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refreshMutation.mutate()}
              loading={refreshMutation.isPending}
              disabled={busy}
            >
              <RefreshCw size={14} />
              Refresh
            </Button>
          </div>
        ) : null}
      </div>

      {viewQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-8">
          Loading integration…
        </p>
      ) : viewQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-8">
          {extractApiError(viewQuery.error).message}
        </p>
      ) : (
        <div className="bg-[color:var(--color-card)] rounded-2xl border border-[color:var(--color-rule)] p-6 space-y-5">
          {/* Connection state + request/retry action. */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-[color:var(--color-ink)]">{conn.hint}</p>
              {view.project ? (
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  {view.project.name ?? 'Project'}
                  {view.project.code ? ` · ${view.project.code}` : ''}
                  {view.project.prefix ? ` · prefix ${view.project.prefix}` : ''}
                </p>
              ) : null}
              {view.lastCheckedAt ? (
                <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)]">
                  Last checked {formatDateTime(view.lastCheckedAt)}
                </p>
              ) : null}
              {view.lastError ? (
                <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                  {view.lastError}
                </p>
              ) : null}
            </div>
            {conn.action ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => requestMutation.mutate()}
                loading={requestMutation.isPending}
                disabled={busy}
                className="shrink-0"
              >
                {conn.action === 'retry' ? 'Retry Connection' : 'Request Connection'}
              </Button>
            ) : null}
          </div>

          <div className="border-t border-[color:var(--color-rule)]" />

          {/* Credentials + Base URL. */}
          <PasswordField
            name="servexa-api-key"
            label="Integration Key"
            autoComplete="off"
            value={apiKeyInput}
            onChange={(e) => {
              setApiKeyInput(e.target.value)
              setApiKeyDirty(true)
            }}
            error={fieldErrors.apiKey}
            placeholder={
              keyState.configured && !apiKeyDirty
                ? `${keyState.masked ?? 'A key'} — leave blank to keep`
                : 'Paste the Servexa key (sup_…)'
            }
            helperText={keyHelper}
            maxLength={200}
          />

          <TextField
            name="servexa-base-url"
            label="Base URL"
            value={baseUrlValue}
            onChange={(e) => setBaseUrlEdit(e.target.value)}
            error={fieldErrors.baseUrl}
            placeholder="https://api.servexa.com/api/v1"
            helperText={baseUrlHelper}
            maxLength={500}
          />

          {/* Public Issue Code (A6a) — non-secret; drives the pre-login form. */}
          <TextField
            name="servexa-public-issue-code"
            label="Public Issue Code"
            value={publicIssueCodeValue}
            onChange={(e) => setPublicIssueCodeEdit(e.target.value)}
            error={fieldErrors.publicIssueCode}
            placeholder="pli_<hex>"
            helperText={publicIssueCodeHelper}
            maxLength={100}
          />

          {/* Chat Widget URL (A8b value 4, N26) — non-secret, but it decides
           *  which script runs inside our app, so it is validated server-side
           *  and every change is audited. Empty = Chat not configured. */}
          <TextField
            name="servexa-chat-widget-url"
            label="Chat Widget URL"
            value={chatWidgetUrlValue}
            onChange={(e) => setChatWidgetUrlEdit(e.target.value)}
            error={fieldErrors.chatWidgetUrl}
            placeholder="https://<servexa-web-host>/…/servexa-chat-widget.js"
            helperText={chatWidgetUrlHelper}
            maxLength={1000}
          />

          {/* Webhook signing secret (A13.4) — optional, encrypted, never shown. */}
          <PasswordField
            name="servexa-webhook-secret"
            label="Webhook Signing Secret"
            autoComplete="off"
            value={webhookSecretInput}
            onChange={(e) => {
              setWebhookSecretInput(e.target.value)
              setWebhookSecretDirty(true)
            }}
            error={fieldErrors.webhookSecret}
            placeholder={
              webhookSecretState.configured
                ? '•••••••• — leave blank to keep'
                : 'whsec_<64 hex> (optional)'
            }
            helperText={webhookSecretHelper}
            maxLength={200}
          />

          {view.warnings && view.warnings.length > 0 ? (
            <div className="space-y-1">
              {view.warnings.map((w, i) => (
                <p
                  key={i}
                  className="text-xs text-[color:var(--color-warning)] flex items-start gap-1.5"
                >
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  {w}
                </p>
              ))}
            </div>
          ) : null}

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={handleSave} loading={saveMutation.isPending} disabled={busy}>
              Save &amp; Connect
            </Button>
            <span className="text-xs text-[color:var(--color-ink-muted)]">
              Saves, requests the connection, then shows the real status above.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
